import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import slugify from 'slugify';

import ContentBlock from '../../components/ContentBlock/ContentBlock';
import FeaturedTestimonials from '../../components/FeaturedTestimonials/FeaturedTestimonials';
import NavigationCards from '../../components/NavigationCards/NavigationCards';
import SplitBlock from '../../components/SplitBlock/SplitBlock';
import Section, { themes } from '../../components/UI/Section/Section';
import ArticleSection from '../ArticleSection/ArticleSection';
import LocationsBanner from '../LocationsBanner/LocationsBanner';
import VideoBlock from '../VideoBlock/VideoBlock';
import LocationLister from '../LocationLister/LocationLister';
import FaqSection from '../FaqSection/FaqSection';
import ConsultantSection from '../ConsultantSection/ConsultantSection';

const types = {
  SECTION_HEADING: 'Page_Blocks_Pagebuilder_SectionHeading',
  CONTENT_BLOCK: 'Page_Blocks_Pagebuilder_LayoutContent',
  SPLIT_BLOCK: 'Page_Blocks_Pagebuilder_LayoutSplitblock',
  CTAS: 'Page_Blocks_Pagebuilder_Ctas',
  NAVIGATION_CARDS: 'Page_Blocks_Pagebuilder_NavigationCards',
  COMMON_BLOCKS: 'Page_Blocks_Pagebuilder_LayoutBlock',
  VIDEO_BLOCK: 'Page_Blocks_Pagebuilder_Video',
  FAQS: 'Page_Blocks_Pagebuilder_Faqs',
  CONSULTANTS: 'Page_Blocks_Pagebuilder_Consultants',
  TESTIMONIALS: 'Page_Blocks_Pagebuilder_Testimonials'
};

const RECENT_BLOG_POSTS_QUERY = graphql`
  query RecentBlogPostsQuery {
    ...RecentPosts
  }
`;

export function getSpacerSize(nextBlock){
  let spacerSize = 'default';

  if (
    nextBlock?.fieldGroupName === types.SPLIT_BLOCK &&
    nextBlock?.layout === 'imageTop'
  ) {
    spacerSize = 'lg';
  }

  return spacerSize;
}

export function getHasSpacer(nextBlock){
  return (
    nextBlock?.fieldGroupName === types.SPLIT_BLOCK 
    || nextBlock?.fieldGroupName === types.NAVIGATION_CARDS
    || nextBlock?.fieldGroupName === types.CONTENT_BLOCK
  )
}

export function getIsImageOffset(prevBlock){
  return (!!prevBlock && prevBlock.fieldGroupName !== types.CONTENT_BLOCK);
}

const PageBuilder = ({ blocks }) => {
  const {
    recentPosts: {
      nodes: recentPosts
    }
  } = useStaticQuery(RECENT_BLOG_POSTS_QUERY);

  return (
    <>
      {blocks?.map(
        ({ fieldGroupName, showInNavbar, navbarLabel, block: blockName, ...item }, index) => {
          const prevBlock = index !== 0 ? blocks[ index - 1 ] : null;
          const nextBlock = index !== blocks.length - 1 ? blocks[ index + 1 ] : null;
          const hasSpacer = getHasSpacer(nextBlock);
          const spacerSize = getSpacerSize(nextBlock);
          const isImageOffset = getIsImageOffset(prevBlock);
          const key = fieldGroupName + index;

          if (fieldGroupName === types.SECTION_HEADING) {
            return (
              <Section
                id={showInNavbar && slugify(navbarLabel, { lower: true })}
                theme={themes.BASIC}
                as="div"
                heading={item.heading}
                key={key}
              />
            );
          }

          if (fieldGroupName === types.CONTENT_BLOCK) {
            return (
              <ContentBlock
                id={showInNavbar && slugify(navbarLabel, { lower: true })}
                body={item.mainBody}
                aside={item.aside}
                hasSpacer={hasSpacer}
                spacerSize={spacerSize} 
                key={key}
              />
            );
          }

          if (fieldGroupName === types.VIDEO_BLOCK) {
            return (
              <VideoBlock 
                {...item} 
                hasSpacer={hasSpacer}
                spacerSize={spacerSize} 
                key={key}
              />
            );
          }

          if (fieldGroupName === types.SPLIT_BLOCK) {
            return (
              <SplitBlock
                {...item}
                id={showInNavbar && slugify(navbarLabel, { lower: true })}
                hasSpacer={hasSpacer}
                hasImageOffset={isImageOffset}
                spacerSize={spacerSize}
                key={key}
              />
            );
          }

          if (
            fieldGroupName === types.COMMON_BLOCKS &&
            blockName === 'testimonials'
          ) {
            const { testimonialCategory, ...other } = item;

            return (
              <FeaturedTestimonials 
                hasSpacer={hasSpacer}
                spacerSize={spacerSize} 
                categoryName={testimonialCategory} 
                key={key} 
                {...other}
                />
            );
          }

          if (
            fieldGroupName === types.COMMON_BLOCKS &&
            blockName === 'latestBlogArticles'
          ) {
            return (
              <ArticleSection 
                {...item} 
                nodes={recentPosts} 
                key={key} 
                sectionProps={{ hasSpacer, spacerSize }} 
                />
            );
          }

          if (
            fieldGroupName === types.COMMON_BLOCKS &&
            (blockName === 'serviceFinder' || blockName === 'locationsFinder')
          ) {
            return (
              <LocationsBanner 
                hasSpacer={hasSpacer}
                spacerSize={spacerSize} 
                filterByService={blockName === 'serviceFinder'} 
                key={key}/>
            );
          }

          if (
            fieldGroupName === types.COMMON_BLOCKS &&
            blockName === 'locationLister'
          ) {
            return (
              <LocationLister 
                hasSpacer={hasSpacer}
                spacerSize={spacerSize}   
                key={key}/>
            );
          }

          if (fieldGroupName === types.NAVIGATION_CARDS) {
            return (
              <NavigationCards
                hasSpacer={hasSpacer}
                spacerSize={spacerSize} 
                isInnerOffsetY={!!prevBlock}
                items={item.navigationCards}
                key={key}
              />
            );
          }
          
          if (fieldGroupName === types.FAQS) {
            return (
              <FaqSection 
                heading={item.heading} 
                nodes={item.faqs} 
                sectionProps={{ 
                  id: showInNavbar && slugify(navbarLabel, { lower: true }),
                  hasSpacer, 
                  spacerSize
                }}
                key={key} />
            )
          }

          if (fieldGroupName === types.CONSULTANTS) {
            return (
              <ConsultantSection 
                heading={item.heading} 
                nodes={item.consultants}
                sectionProps={{ 
                  id: showInNavbar && slugify(navbarLabel, { lower: true }),
                  hasSpacer, 
                  spacerSize
                }}
                key={key} />
            )
          } 

          if (fieldGroupName === types.TESTIMONIALS) {
            return (
              <FeaturedTestimonials 
                id={showInNavbar && slugify(navbarLabel, { lower: true })}
                hasSpacer={hasSpacer}
                spacerSize={spacerSize} 
                categoryName=''
                nodes={item.testimonials.map(t => t.testimonial)} 
                key={key} 
                />
            )
          }

          return null;
        }
      )}
    </>
  );
};

export default PageBuilder;
